import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import type {
  HrUser,
  Leave,
  CreateUserPayload,
  UpdateUserPayload,
  SubmitLeavePayload,
  LeaveStatus,
  LeaveTypeConfig,
  CreateLeaveTypePayload,
  UpdateLeaveTypePayload,
  LeaveBalance,
  AllocateBalancePayload,
  BulkAllocatePayload,
  Holiday,
  CreateHolidayPayload,
  UpdateHolidayPayload,
} from '../types';
import type { CreateUserRole } from '../components/createUserForm';

// ─── Users (Team & Access) ────────────────────────────────────────────────

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => {
      const { data } = await api.get<{ users: HrUser[] }>('/api/admin/users');
      return data.users ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useUserMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-users'] });

  const createUser = useMutation({
    mutationFn: async (payload: CreateUserPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/admin/users', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateUser = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateUserPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/admin/users/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const setUserRole = useMutation({
    mutationFn: async ({ id, role }: { id: string; role: CreateUserRole }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/admin/users/${id}/role`, { role });
      return data;
    },
    onSuccess: invalidate,
  });

  // Clears a login lockout (3 failed attempts) so the user can log back in
  // immediately instead of waiting out the lock window.
  const unlockUser = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<{ ok: boolean }>(`/api/admin/users/${id}/unlock-login`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createUser, updateUser, setUserRole, unlockUser };
}

// ─── Leaves ──────────────────────────────────────────────────────────────

export function useLeaves() {
  return useQuery({
    queryKey: ['leaves'],
    queryFn: async () => {
      const { data } = await api.get<{ leaves: Leave[] }>('/api/leaves');
      return data.leaves ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeaveMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => Promise.all([
    queryClient.invalidateQueries({ queryKey: ['leaves'] }),
    queryClient.invalidateQueries({ queryKey: ['leave-balances'] }),
    queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  ]);

  const submitLeave = useMutation({
    mutationFn: async (payload: SubmitLeavePayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/leaves', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const reviewLeave = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Extract<LeaveStatus, 'approved' | 'rejected'> }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/leaves/${id}`, { status });
      return data;
    },
    onSuccess: invalidate,
  });

  const cancelLeave = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { data } = await api.post<{ ok: boolean }>(`/api/leaves/${id}/cancel`, { reason });
      return data;
    },
    onSuccess: invalidate,
  });

  return { submitLeave, reviewLeave, cancelLeave };
}

// ─── Leave Types ─────────────────────────────────────────────────────────

export function useLeaveTypes(includeInactive = false) {
  return useQuery({
    queryKey: ['leave-types', includeInactive],
    queryFn: async () => {
      const params = includeInactive ? '?all=true' : '';
      const { data } = await api.get<{ leaveTypes: LeaveTypeConfig[] }>(`/api/leave-types${params}`);
      return data.leaveTypes ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeaveTypeMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave-types'] });

  const createLeaveType = useMutation({
    mutationFn: async (payload: CreateLeaveTypePayload) => {
      const { data } = await api.post<{ ok: boolean; id: string; code: string }>('/api/leave-types', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateLeaveType = useMutation({
    mutationFn: async ({ code, payload }: { code: string; payload: UpdateLeaveTypePayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/leave-types/${code}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteLeaveType = useMutation({
    mutationFn: async (code: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/leave-types/${code}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createLeaveType, updateLeaveType, deleteLeaveType };
}

// ─── Leave Balances ──────────────────────────────────────────────────────

export function useLeaveBalances(year?: number) {
  const targetYear = year || new Date().getFullYear();
  return useQuery({
    queryKey: ['leave-balances', targetYear],
    queryFn: async () => {
      const { data } = await api.get<{ balances: LeaveBalance[]; year: number }>(`/api/leave-balances?year=${targetYear}`);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useAllLeaveBalances(year?: number, department?: string) {
  const targetYear = year || new Date().getFullYear();
  return useQuery({
    queryKey: ['leave-balances-all', targetYear, department],
    queryFn: async () => {
      let url = `/api/leave-balances?all=true&year=${targetYear}`;
      if (department) url += `&department=${department}`;
      const { data } = await api.get<{ balances: LeaveBalance[]; year: number }>(url);
      return data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useLeaveBalanceMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['leave-balances'] });

  const allocate = useMutation({
    mutationFn: async (payload: AllocateBalancePayload) => {
      const { data } = await api.post<{ ok: boolean }>('/api/leave-balances/allocate', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const bulkAllocate = useMutation({
    mutationFn: async (payload: BulkAllocatePayload) => {
      const { data } = await api.post<{ ok: boolean; count: number }>('/api/leave-balances/bulk-allocate', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  return { allocate, bulkAllocate };
}

// ─── Holidays ────────────────────────────────────────────────────────────

export function useHolidays(year?: number) {
  const targetYear = year || new Date().getFullYear();
  return useQuery({
    queryKey: ['holidays', targetYear],
    queryFn: async () => {
      const { data } = await api.get<{ holidays: Holiday[]; year: number }>(`/api/holidays?year=${targetYear}`);
      return data.holidays ?? [];
    },
    placeholderData: keepPreviousData,
  });
}

export function useHolidayMutations() {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['holidays'] });

  const createHoliday = useMutation({
    mutationFn: async (payload: CreateHolidayPayload) => {
      const { data } = await api.post<{ ok: boolean; id: string }>('/api/holidays', payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const updateHoliday = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateHolidayPayload }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/holidays/${id}`, payload);
      return data;
    },
    onSuccess: invalidate,
  });

  const deleteHoliday = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<{ ok: boolean }>(`/api/holidays/${id}`);
      return data;
    },
    onSuccess: invalidate,
  });

  return { createHoliday, updateHoliday, deleteHoliday };
}
